/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import OverviewSection from '@apps/components/OverviewSection';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import {
  CarOverviewNavBarItem,
  navItems,
} from '../CarOverviewHeaderNavBarItem/settings';

import CarOverviewSectionContentProps, {
  CarOverviewSectionContentRefProps,
  CarOverviewSectionType,
} from './props';
import {
  Error404Button,
  Error404Caption,
  Error404CaptionWrapper,
  Error404Container,
  Error404Title,
} from './styles';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const CarOverviewSectionContentView: React.ForwardRefRenderFunction<
  CarOverviewSectionContentRefProps,
  CarOverviewSectionContentProps
> = (props, ref) => {
  const { data, containerRef, navHeaderRef, navItemRef } = props;

  // ======================= HOOKS

  const getDataByDescriptionName = (descriptionName: string) =>
    data?.filter(
      (item: any) => item.new_car_setting_description_name === descriptionName
    );

  const overviewData = getDataByDescriptionName('Overview');
  const interiorData = getDataByDescriptionName('Interior');
  const exteriorData = getDataByDescriptionName('Exterior');
  const featureData = getDataByDescriptionName('Features');
  const performanceData = getDataByDescriptionName('Performance');
  const comfortData = getDataByDescriptionName('Comfort');
  const wrapUpData = getDataByDescriptionName('Wrap Up');

  // const { spec, serviceData } = createSectionData(data);
  const { sm, md } = useGetResponsive();
  const { t } = useTranslation('common');

  // ======================= EVENTS
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToSection = (key: CarOverviewNavBarItem) => {
    const container = containerRef.current;
    if (container) {
      const rectTop =
        document.getElementById(`carOverviewSection_${key}`)?.offsetTop ?? 0;
      container.scrollTo({
        top: rectTop,
        behavior: 'smooth',
      });
    }
  };

  // ======================= EFFECTS
  useImperativeHandle(
    ref,
    () => ({
      scrollToSection,
    }),
    [scrollToSection]
  );

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;

      const navHeader = navHeaderRef.current;
      const navElement = navItemRef.current?.element;
      if (container && navHeader && navElement) {
        const elements = container.querySelectorAll(
          '[id^="carOverviewSection_"]'
        );

        const navElements = navElement?.querySelectorAll(
          '[id^="carOverviewNavItem_"]'
        );

        let newCurrentItem = 'Summary';

        elements.forEach((element) => {
          const elementId = element.id.replace('carOverviewSection_', '');

          const rectTop =
            document.getElementById(`carOverviewSection_${elementId}`)
              ?.offsetTop ?? 0;

          const navHeight =
            document.getElementById('carOverviewContainer')?.scrollTop ?? 0;

          const newNavHeight = navHeight + 80;

          if (rectTop < newNavHeight) {
            newCurrentItem = element.id.replace('carOverviewSection_', '');
          }
        });

        if (newCurrentItem !== navItemRef.current?.focusedNavItem) {
          navItemRef.current?.setFocusedNavItem(newCurrentItem as any);
          // Find the index of the item with key
          const loanCalculatorIndex = navItems.findIndex(
            (item) => item.key === newCurrentItem
          );
          // Create a new array with items up to the current key item
          const filteredNavItems = navItems
            .slice(0, loanCalculatorIndex + 1)
            ?.map((item) => item.key);

          let totalWidth = 0;

          if (navElements) {
            navElements.forEach((element) => {
              const elementId = element.id.replace('carOverviewNavItem_', '');

              const activeNavItem = filteredNavItems.includes(
                elementId as CarOverviewNavBarItem
              );

              if (activeNavItem) {
                totalWidth += element.getBoundingClientRect().width;
              }
            });
          }

          // TO AUTO CENTER IN MOBILE VIEW
          if (sm || md) {
            const navHeaderWidth = navHeader.getBoundingClientRect().width;
            const navHeaderCenter = navHeaderWidth / 2;
            const scrollLeft = totalWidth - navHeaderCenter - 20;
            navElement?.scrollTo({
              left: scrollLeft,
              behavior: 'smooth',
            });
          }
        }
      }
    };

    const currentSectionRef = containerRef.current;
    currentSectionRef?.addEventListener('scroll', handleScroll);
    return () => {
      currentSectionRef?.removeEventListener('scroll', handleScroll);
    };
  }, [
    containerRef,
    md,
    navHeaderRef,
    navItemRef,
    navItemRef?.current?.focusedNavItem,
    sm,
  ]);

  // ======================= SECTION DATA
  const sections: CarOverviewSectionType[] = [
    {
      key: 'Summary',
      title: 'Summary',
      data: overviewData,
    },
    {
      key: 'Interior',
      title: 'Interior',
      data: interiorData,
    },
    {
      key: 'Exterior',
      title: 'Exterior',
      data: exteriorData,
    },
    {
      key: 'Features',
      title: 'Features',
      data: featureData,
    },
    {
      key: 'Performance',
      title: 'Performance',
      data: performanceData,
    },
    {
      key: 'Comfort',
      title: 'Comfort',
      data: comfortData,
    },
    {
      key: 'WrapUp',
      title: 'Wrap Up',
      data: wrapUpData,
    },
  ];

  // ======================= VIEW
  const renderError404 = () => {
    return (
      <Error404Container
        style={{
          textDecoration: 'none',
        }}
      >
        <div
          style={{
            top: '20%',
            width: '100%',
            position: 'absolute',
          }}
        >
          <Error404Title>404</Error404Title>
          <Error404CaptionWrapper>
            <Error404Caption>{t('404caption')}</Error404Caption>
          </Error404CaptionWrapper>
          {!true && (
            <Link href={'/'}>
              <Error404Button>{t('backHome')}</Error404Button>
            </Link>
          )}
        </div>
      </Error404Container>
    );
  };

  if (!data) return renderError404();
  return (
    <Stack id="carOverview-scrollactive" gap={{ xs: 10, lg: 12.5 }}>
      {sections.map((section) => {
        return (
          <Box
            key={`${section.key}`}
            id={`carOverviewSection_${section.key}`}
            sx={{ padding: { xs: 4, md: 5 } }}
          >
            <OverviewSection title={section.title} data={section.data} />
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export const CarOverviewSectionContent = forwardRef(
  CarOverviewSectionContentView
);
